/**
 * Gemeinsame Datenbeschaffung für die Rezeptliste und die Suche.
 *
 * Rezepte, Kategorien und Schlagwörter werden einmal geladen und im Speicher
 * gehalten. Für den Filter nach Zutaten werden zusätzlich die Zutatenlisten
 * der Rezepte geholt – aber nur, wenn dieser Filter überhaupt gesetzt ist.
 */
import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client.ts';
import type { Category, RecipeDetail, RecipeSummary, Tag } from '../api/types.ts';
import { buildSearchIndex } from '../lib/search.ts';
import { useQuery } from '../api/useQuery.ts';

/** Geladene Grunddaten samt Suchindex. */
export interface CatalogData {
  readonly recipes: readonly RecipeSummary[];
  readonly categories: readonly Category[];
  readonly tags: readonly Tag[];
  readonly loading: boolean;
  readonly error: string | null;
  reload: () => void;
}

/** Lädt Rezepte, Kategorien und Schlagwörter. */
export function useCatalog(): CatalogData {
  const recipes = useQuery<{ recipes: RecipeSummary[] }>('/api/recipes');
  const categories = useQuery<{ categories: Category[] }>('/api/categories');
  const tags = useQuery<{ tags: Tag[] }>('/api/tags');

  return {
    recipes: recipes.data?.recipes ?? [],
    categories: categories.data?.categories ?? [],
    tags: tags.data?.tags ?? [],
    loading: recipes.loading || categories.loading || tags.loading,
    error: recipes.error ?? categories.error ?? tags.error,
    reload: () => {
      recipes.reload();
      categories.reload();
      tags.reload();
    },
  };
}

/** Suchindex über die Rezepte. */
export function useSearchIndex(recipes: readonly RecipeSummary[]) {
  return useMemo(() => buildSearchIndex(recipes), [recipes]);
}

/**
 * Zutatenlisten je Rezept, aber nur wenn sie gebraucht werden.
 * Das hält die Liste schnell, solange niemand nach Zutaten filtert.
 */
export function useIngredientIndex(
  recipes: readonly RecipeSummary[],
  needed: boolean,
): ReadonlyMap<string, readonly string[]> {
  const [byId, setById] = useState<Map<string, string[]>>(new Map());

  useEffect(() => {
    if (!needed || recipes.length === 0) {
      return;
    }
    let alive = true;
    void Promise.all(
      recipes.map(async (recipe) => {
        const result = await api.get<{ recipe: RecipeDetail }>(`/api/recipes/${recipe.id}`);
        return [recipe.id, result.recipe.ingredients.map((entry) => entry.name)] as const;
      }),
    )
      .then((entries) => {
        if (alive) {
          setById(new Map(entries));
        }
      })
      .catch(() => {
        // Bei einem Fehler bleibt die Zutatenliste leer; die anderen Filter wirken weiter.
      });
    return () => {
      alive = false;
    };
  }, [needed, recipes]);

  return byId;
}
